import { type QueryKey, skipToken, useQuery } from '@tanstack/react-query';
import { isObject } from 'lodash';

import { Discussions } from '@air/api';

import { isCommentUnresolvableError } from '~/swr-hooks/discussions/isCommentUnresolvableError';

export const PRIVATE_COMMENT_KEY = 'PRIVATE_COMMENT';

const COMMENT_TRANSIENT_RETRIES = 1;

export type PrivateCommentKey = [typeof PRIVATE_COMMENT_KEY, UsePrivateCommentParams];

export const getPrivateCommentKey = (params: UsePrivateCommentParams): PrivateCommentKey => [
  PRIVATE_COMMENT_KEY,
  params,
];

export const isPrivateCommentKey = (key: QueryKey): key is PrivateCommentKey =>
  Array.isArray(key) && key[0] === PRIVATE_COMMENT_KEY && isObject(key[1]);

export type UsePrivateCommentParams = Omit<NonNullable<Parameters<typeof Discussions.Comments.get>[0]>, 'commentId'> & {
  commentId: string | undefined;
};

export type UsePrivateCommentOptions = {
  enabled?: boolean;
};

export const usePrivateComment = (params: UsePrivateCommentParams, options?: UsePrivateCommentOptions) => {
  const { commentId } = params;

  return useQuery({
    enabled: !!commentId && options?.enabled !== false,
    queryFn: commentId ? () => Discussions.Comments.get({ commentId }) : skipToken,
    queryKey: getPrivateCommentKey(params),
    retry: (failureCount, error) => !isCommentUnresolvableError(error) && failureCount < COMMENT_TRANSIENT_RETRIES,
    throwOnError: false,
  });
};
